import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, BookOpen, Download, FileSpreadsheet, FileUp, ListChecks, Lock, Pencil, Plus, Printer, Search, Settings2, Trash2, X } from "lucide-react";
import {
  ApiError,
  type EarhivCategoryDto,
  type EarhivCategoryPayload,
  type EarhivEntryDto,
  type EarhivEntryPayload,
  type EarhivFileDto,
  type EarhivSettingsDto,
} from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const MODULE_KEY = "earhiv";

type TabKey = "knjiga" | "dokumenta" | "kategorije" | "podesavanja";

const DOC_TYPE_LABELS: Record<string, string> = {
  EINVOICE_OUT: "Izlazne e-fakture",
  EINVOICE_IN: "Ulazne e-fakture",
  FISCAL_RECEIPT: "Fiskalni računi",
  BANK_STATEMENT: "Bankovni izvodi",
  EXPENSE: "Troškovi",
  CALCULATION: "Kalkulacije",
  WAREHOUSE_ORDER: "Nalozi magacinu",
  QUOTE: "Ponude i predračuni",
  CONTRACT: "Ugovori",
  SHIFT: "Smene i izveštaji kase",
};

const inputCls = "mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const areaCls = "mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none";

function errMsg(err: unknown, fallback: string) {
  if (err instanceof ApiError) {
    const m = err.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return fallback;
}

function fmtDate(iso: string | null) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${Number(d)}.${Number(m)}.${y}.`;
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Zatvori">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Arhivska knjiga */

function EntryModal({
  entry,
  year,
  categories,
  onClose,
}: {
  entry: EarhivEntryDto | null;
  year: number;
  categories: EarhivCategoryDto[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!entry;
  const [categoryId, setCategoryId] = useState(entry?.categoryId ?? categories[0]?.id ?? "");
  const [entryYear, setEntryYear] = useState(String(entry?.year ?? year));
  const [yearTo, setYearTo] = useState(entry?.yearTo ? String(entry.yearTo) : "");
  const [content, setContent] = useState(entry?.content ?? "");
  const [quantity, setQuantity] = useState(entry?.quantity ?? "");
  const [location, setLocation] = useState(entry?.location ?? "");
  const [minutesNumber, setMinutesNumber] = useState(entry?.minutesNumber ?? "");
  const [minutesDate, setMinutesDate] = useState(entry?.minutesDate ?? "");
  const [note, setNote] = useState(entry && entry.locked ? "" : (entry?.note ?? ""));

  const save = useMutation({
    mutationFn: () => {
      const payload: EarhivEntryPayload = {
        yearTo: yearTo ? Number(yearTo) : null,
        content: content || null,
        quantity: quantity || null,
        location: location || null,
        minutesNumber: minutesNumber || null,
        minutesDate: minutesDate || null,
        note: note || null,
      };
      return isEdit
        ? api.updateEarhivEntry(entry!.id, payload)
        : api.createEarhivEntry({ ...payload, categoryId, year: Number(entryYear) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["earhiv-entries"] });
      qc.invalidateQueries({ queryKey: ["earhiv-years"] });
      onClose();
    },
  });

  const locked = !!entry?.locked;
  return (
    <Modal title={isEdit ? `Red ${entry!.classCode} / ${entry!.rowNumber}` : "Novi ručni red"} onClose={onClose}>
      <div className="space-y-3 px-5 py-5">
        {save.isError && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{errMsg(save.error, "Čuvanje nije uspelo.")}</p>
        )}
        {locked && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Red je zaključan (predat arhivu). Može da se doda samo zapisnik (rubrika 10) i obavezna napomena (rubrika 11).
          </p>
        )}
        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-slate-700">Kategorija (klasifikaciona oznaka) *</label>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={inputCls}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} – {c.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700">Godina nastanka *</label>
            <input type="number" value={entryYear} disabled={isEdit} onChange={(e) => setEntryYear(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">do godine (ako je raspon)</label>
            <input type="number" value={yearTo} disabled={locked} onChange={(e) => setYearTo(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Sadržaj</label>
          <input value={content} disabled={locked} onChange={(e) => setContent(e.target.value)} placeholder="npr. Registratori – fakture 2019" className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Količina dokumentarnog materijala</label>
          <input value={quantity} disabled={locked} onChange={(e) => setQuantity(e.target.value)} placeholder="npr. 4 registratora" className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Prostorija, polica / uređaj i lokacija</label>
          <input value={location} disabled={locked} onChange={(e) => setLocation(e.target.value)} placeholder="npr. Kancelarija, orman 2, polica 3" className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700">Broj zapisnika (izdvajanje)</label>
            <input value={minutesNumber} onChange={(e) => setMinutesNumber(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Datum zapisnika</label>
            <input type="date" value={minutesDate} onChange={(e) => setMinutesDate(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Primedba{locked ? " *" : ""}</label>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={areaCls} />
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
        <Button variant="secondary" onClick={onClose}>
          Otkaži
        </Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending || (!isEdit && !categoryId)}>
          {save.isPending ? "Čuvam..." : "Sačuvaj"}
        </Button>
      </div>
    </Modal>
  );
}

function BookTab() {
  const qc = useQueryClient();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [editing, setEditing] = useState<EarhivEntryDto | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const years = useQuery({ queryKey: ["earhiv-years"], queryFn: () => api.listEarhivYears() });
  const entries = useQuery({ queryKey: ["earhiv-entries", year], queryFn: () => api.listEarhivEntries(year) });
  const cats = useQuery({ queryKey: ["earhiv-categories"], queryFn: () => api.listEarhivCategories() });
  const settings = useQuery({ queryKey: ["earhiv-settings"], queryFn: () => api.getEarhivSettings() });

  const yearRows = entries.data ?? [];
  const yearLocked = yearRows.length > 0 && yearRows.every((r) => r.locked);

  const yearOptions = Array.from(
    new Set([thisYear, thisYear - 1, thisYear - 2, ...(years.data ?? []).map((y) => y.year)]),
  ).sort((a, b) => b - a);

  const generate = useMutation({
    mutationFn: () => api.generateEarhivYear(year),
    onSuccess: (r) => {
      setErr(null);
      setMsg(
        `Godina ${r.year}: ${r.created} novih redova, ${r.updated} osveženo` +
          (r.skippedLocked ? `, ${r.skippedLocked} zaključanih preskočeno` : "") +
          (r.skippedEmpty ? `, ${r.skippedEmpty} kategorija bez dokumenata` : "") +
          ".",
      );
      qc.invalidateQueries({ queryKey: ["earhiv-entries"] });
      qc.invalidateQueries({ queryKey: ["earhiv-years"] });
    },
    onError: (e) => {
      setMsg(null);
      setErr(errMsg(e, "Punjenje knjige nije uspelo."));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteEarhivEntry(id),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ["earhiv-entries"] });
    },
    onError: (e) => setErr(errMsg(e, "Brisanje nije uspelo.")),
  });

  const lockYear = useMutation({
    mutationFn: () => api.lockEarhivYear(year),
    onSuccess: (r) => {
      setErr(null);
      setMsg(`Zaključano ${r.locked} redova za ${r.year}. godinu.`);
      qc.invalidateQueries({ queryKey: ["earhiv-entries"] });
    },
    onError: (e) => setErr(errMsg(e, "Zaključavanje nije uspelo.")),
  });

  const [exporting, setExporting] = useState(false);
  const runExport = async (kind: "pdf" | "xlsx", all: boolean) => {
    setExporting(true);
    setErr(null);
    try {
      const url = await api.fetchEarhivExportBlobUrl(kind, all ? undefined : year);
      if (kind === "pdf") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `arhivska-knjiga${all ? "" : `-${year}`}.xlsx`;
        a.click();
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Izvoz nije uspeo.");
    } finally {
      setExporting(false);
    }
  };

  const noCategories = cats.data && cats.data.length === 0;
  const noSettings = settings.data && !settings.data.archiveName;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Arhivska knjiga je zakonska evidencija dokumentacije firme. Ne upisuje se svaki dokument, nego jedna vrsta
        dokumenata za jednu godinu čini jedan red. HAFiskal sam popunjava redove iz računa, e-faktura, izvoda i drugih
        dokumenata; papirnu građu dodajete ručno.
      </p>

      {noCategories && (
        <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Prvo preuzmite listu kategorija (kartica „Lista kategorija“) – bez nje knjiga ne može da se popuni.
        </p>
      )}
      {!noCategories && noSettings && (
        <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Upišite nadležni arhiv i broj saglasnosti u kartici „Podešavanja“ – ti podaci idu u rubriku 7 svakog reda.
        </p>
      )}
      {msg && <p className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{msg}</p>}
      {err && <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</p>}

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div>
          <label className="block text-xs font-medium uppercase text-slate-500">Godina</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="mt-1 h-10 rounded-lg border border-border px-3 text-sm">
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <Button onClick={() => generate.mutate()} disabled={generate.isPending || !!noCategories}>
          {generate.isPending ? "Punim..." : `Zatvori / osveži godinu ${year}`}
        </Button>
        <Button variant="secondary" onClick={() => setShowNew(true)} disabled={!!noCategories}>
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          Dodaj ručni red
        </Button>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => runExport("pdf", false)} disabled={exporting}>
            <Printer className="mr-1 h-4 w-4" aria-hidden />
            Štampaj / PDF ({year})
          </Button>
          <Button variant="secondary" onClick={() => runExport("pdf", true)} disabled={exporting}>
            <Printer className="mr-1 h-4 w-4" aria-hidden />
            Ceo prepis (sve godine)
          </Button>
          <Button variant="secondary" onClick={() => runExport("xlsx", false)} disabled={exporting}>
            <FileSpreadsheet className="mr-1 h-4 w-4" aria-hidden />
            Excel ({year})
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              window.confirm(
                `Zaključati sve redove za ${year}. godinu? To radite kad je prepis predat arhivu; kasnije izmene traže napomenu.`,
              ) && lockYear.mutate()
            }
            disabled={lockYear.isPending || yearLocked || yearRows.length === 0}
          >
            <Lock className="mr-1 h-4 w-4" aria-hidden />
            {yearLocked ? `Godina ${year} je zaključana` : `Zaključaj ${year}`}
          </Button>
        </div>
      </div>

      {entries.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : entries.data && entries.data.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
          <table className="w-full min-w-[1200px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-slate-500">
                <th className="px-3 py-2">Redni broj</th>
                <th className="px-3 py-2">Datum upisa</th>
                <th className="px-3 py-2">Godina nastanka</th>
                <th className="px-3 py-2">Sadržaj</th>
                <th className="px-3 py-2">Klasifikaciona oznaka</th>
                <th className="px-3 py-2">Rok čuvanja iz Liste kategorija</th>
                <th className="px-3 py-2">Broj saglasnosti na Listu kategorija</th>
                <th className="px-3 py-2">Količina dokumentarnog materijala</th>
                <th className="px-3 py-2">Prostorije i police/uređaji za skladištenje sa lokacijom</th>
                <th className="px-3 py-2">Broj i datum zapisnika</th>
                <th className="px-3 py-2">Primedba</th>
                <th className="w-24 px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {entries.data.map((e) => (
                <tr key={e.id} className="border-b border-border align-top last:border-0">
                  <td className="px-3 py-2">{e.rowNumber}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.entryDate)}</td>
                  <td className="px-3 py-2">{e.yearTo ? `${e.year}–${e.yearTo}` : e.year}</td>
                  <td className="px-3 py-2">
                    {e.content}
                    {e.origin === "MANUAL" && <span className="ml-1 rounded bg-slate-100 px-1 text-xs text-slate-500">ručno</span>}
                  </td>
                  <td className="px-3 py-2">{e.classCode}</td>
                  <td className="px-3 py-2">{e.retentionLabel}</td>
                  <td className="px-3 py-2">{e.consentNumber}</td>
                  <td className="px-3 py-2">{e.quantity}</td>
                  <td className="px-3 py-2">{e.location}</td>
                  <td className="px-3 py-2">{[e.minutesNumber, fmtDate(e.minutesDate)].filter(Boolean).join(" / ")}</td>
                  <td className="px-3 py-2">{e.note}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-2 text-slate-400">
                      {e.locked && <Lock className="h-4 w-4" aria-label="Zaključan red" />}
                      <button onClick={() => setEditing(e)} className="hover:text-brand" aria-label="Izmeni">
                        <Pencil className="h-4 w-4" aria-hidden />
                      </button>
                      {e.origin === "MANUAL" && !e.locked && (
                        <button
                          onClick={() => window.confirm("Obrisati ovaj ručni red?") && remove.mutate(e.id)}
                          className="hover:text-red-600"
                          aria-label="Obriši"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={BookOpen}
          title={`Nema redova za ${year}. godinu`}
          description="Kliknite „Zatvori / osveži godinu“ da se knjiga popuni iz dokumenata, ili dodajte ručni red."
        />
      )}

      {(showNew || editing) && (
        <EntryModal
          entry={editing}
          year={year}
          categories={(cats.data ?? []).filter((c) => c.isActive)}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Dokumenta (upload) */

function fmtSize(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** Bezbedan tip za pregled u pregledaču; za sve ostalo (Word, Excel, HTML...) pregleda nema, samo preuzimanje. */
function previewType(f: { fileName: string; mimeType: string | null }): string | null {
  const ext = f.fileName.toLowerCase().split(".").pop() ?? "";
  if (ext === "pdf") return "application/pdf";
  if (ext === "png") return "image/png";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "gif") return "image/gif";
  if (ext === "webp") return "image/webp";
  if (ext === "txt") return "text/plain; charset=utf-8";
  return null;
}

function FilesTab() {
  const qc = useQueryClient();
  const thisYear = new Date().getFullYear();
  const [categoryId, setCategoryId] = useState("");
  const [year, setYear] = useState(String(thisYear));
  const [description, setDescription] = useState("");
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const cats = useQuery({ queryKey: ["earhiv-categories"], queryFn: () => api.listEarhivCategories() });
  const usage = useQuery({ queryKey: ["earhiv-storage"], queryFn: () => api.getEarhivStorage() });
  const files = useQuery({
    queryKey: ["earhiv-files", filter],
    queryFn: () => api.listEarhivFiles(filter ? { categoryId: filter } : undefined),
  });

  const needle = search.trim().toLowerCase();
  const shown = (files.data ?? []).filter(
    (f: EarhivFileDto) =>
      !needle ||
      `${f.fileName} ${f.description ?? ""} ${f.categoryCode} ${f.categoryName} ${f.year}`.toLowerCase().includes(needle),
  );

  const preview = async (f: EarhivFileDto) => {
    const type = previewType(f);
    if (!type) return;
    const w = window.open("", "_blank");
    try {
      const url = await api.fetchEarhivFileBlobUrl(f.id, type);
      if (w) w.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      w?.close();
      setErr(e instanceof Error ? e.message : "Pregled nije uspeo.");
    }
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["earhiv-files"] });
    qc.invalidateQueries({ queryKey: ["earhiv-storage"] });
    qc.invalidateQueries({ queryKey: ["earhiv-entries"] });
    qc.invalidateQueries({ queryKey: ["earhiv-years"] });
  };

  const onPick = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    if (!categoryId) {
      setErr("Prvo izaberite kategoriju.");
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    let ok = 0;
    try {
      for (const f of Array.from(list)) {
        await api.uploadEarhivFile(f, categoryId, Number(year), description || undefined);
        ok++;
      }
      setDescription("");
    } catch (e) {
      setErr(errMsg(e, "Slanje fajla nije uspelo."));
    } finally {
      if (ok > 0) setMsg(`Poslato fajlova: ${ok}. Red u arhivskoj knjizi je osvežen.`);
      setBusy(false);
      refresh();
    }
  };

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteEarhivFile(id),
    onSuccess: () => {
      setErr(null);
      refresh();
    },
    onError: (e) => setErr(errMsg(e, "Brisanje nije uspelo.")),
  });

  const pct = usage.data ? Math.min(100, (usage.data.usedBytes / usage.data.quotaBytes) * 100) : 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Ovde šaljete dokumenta koja nemaju svoj izvor u HAFiskalu (sudska rešenja, ugovori, skenirana građa). Izaberite
        kategoriju i godinu, pa fajl. Sadržaj se šifruje pre čuvanja, a arhivska knjiga se sama dopuni brojem i veličinom.
      </p>
      {msg && <p className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{msg}</p>}
      {err && <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</p>}
      {usage.data?.backend === "NONE" && (
        <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Skladište za e-arhiv još nije podešeno – slanje fajlova trenutno nije moguće. Obratite se podršci.
        </p>
      )}

      {usage.data && (
        <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <div className="flex justify-between text-sm text-slate-600">
            <span>Zauzeto: {fmtSize(usage.data.usedBytes)} od {fmtSize(usage.data.quotaBytes)}</span>
            <span>{usage.data.files} fajlova</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <div className="grid gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle md:grid-cols-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-700">Kategorija *</label>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={inputCls}>
            <option value="">— izaberite —</option>
            {(cats.data ?? [])
              .filter((c) => c.isActive)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} – {c.name}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Godina nastanka *</label>
          <input type="number" value={year} onChange={(e) => setYear(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Opis (nije obavezan)</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="npr. Rešenje br. 123/25" className={inputCls} />
        </div>
        <div className="md:col-span-4">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:opacity-90">
            <FileUp className="h-4 w-4" aria-hidden />
            {busy ? "Šaljem..." : "Izaberi fajlove i pošalji"}
            <input
              type="file"
              multiple
              disabled={busy}
              className="hidden"
              onChange={(e) => {
                void onPick(e.target.files);
                e.target.value = "";
              }}
            />
          </label>
          <span className="ml-3 text-xs text-slate-400">Do 50 MB po fajlu.</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-sm text-slate-600">Prikaži:</label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 rounded-lg border border-border px-2 text-sm">
          <option value="">sve kategorije</option>
          {(cats.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} – {c.name}
            </option>
          ))}
        </select>
        <div className="relative ml-2 w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pretraga: naziv, opis, kategorija, godina"
            className="h-9 w-full rounded-lg border border-border pl-8 pr-2 text-sm"
          />
        </div>
      </div>

      {files.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : files.data && files.data.length > 0 && shown.length === 0 ? (
        <EmptyState icon={Search} title="Nema rezultata" description="Nijedan dokument ne odgovara pretrazi." />
      ) : files.data && files.data.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-slate-500">
                <th className="px-3 py-2">Fajl</th>
                <th className="px-3 py-2">Kategorija</th>
                <th className="px-3 py-2">Godina</th>
                <th className="px-3 py-2">Veličina</th>
                <th className="px-3 py-2">Poslato</th>
                <th className="w-20 px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {shown.map((f: EarhivFileDto) => (
                <tr key={f.id} className="border-b border-border last:border-0">
                  <td className="px-3 py-2">
                    {f.fileName}
                    {f.description && <div className="text-xs text-slate-400">{f.description}</div>}
                    <div className="font-mono text-[10px] text-slate-300" title="SHA-256 otisak originala">{f.sha256.slice(0, 16)}…</div>
                  </td>
                  <td className="px-3 py-2">{f.categoryCode} – {f.categoryName}</td>
                  <td className="px-3 py-2">{f.year}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtSize(f.sizeBytes)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(f.createdAt).toLocaleDateString("sr-RS")}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2 text-slate-400">
                      <button
                        onClick={() => void preview(f)}
                        disabled={!previewType(f)}
                        className="hover:text-brand disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label="Pogledaj"
                        title={previewType(f) ? "Pogledaj" : "Pregled nije moguć za ovu vrstu fajla – preuzmite ga"}
                      >
                        <Search className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        onClick={() => api.downloadEarhivFile(f.id, f.fileName).catch((e) => setErr(e instanceof Error ? e.message : "Preuzimanje nije uspelo."))}
                        className="hover:text-brand"
                        aria-label="Preuzmi"
                      >
                        <Download className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        onClick={() => window.confirm(`Obrisati fajl „${f.fileName}“?`) && remove.mutate(f.id)}
                        className="hover:text-red-600"
                        aria-label="Obriši"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState icon={FileUp} title="Još nema poslatih dokumenata" description="Izaberite kategoriju i pošaljite prvi fajl." />
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ Lista kategorija */

function CategoryModal({ cat, onClose }: { cat: EarhivCategoryDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState(cat?.code ?? "");
  const [name, setName] = useState(cat?.name ?? "");
  const [groupName, setGroupName] = useState(cat?.groupName ?? "");
  const [permanent, setPermanent] = useState(cat?.permanent ?? false);
  const [years, setYears] = useState(cat?.retentionYears ? String(cat.retentionYears) : "");
  const [note, setNote] = useState(cat?.retentionNote ?? "");
  const [docType, setDocType] = useState(cat?.docType ?? "");
  const [isActive, setIsActive] = useState(cat?.isActive ?? true);

  const save = useMutation({
    mutationFn: () => {
      const payload: EarhivCategoryPayload = {
        code,
        name,
        groupName: groupName || null,
        permanent,
        retentionYears: permanent ? null : Number(years),
        retentionNote: note || null,
        ruleSource: cat?.ruleSource ?? null,
        docType: docType || null,
        isActive,
      };
      return cat ? api.updateEarhivCategory(cat.id, payload) : api.createEarhivCategory(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["earhiv-categories"] });
      onClose();
    },
  });

  return (
    <Modal title={cat ? "Izmena kategorije" : "Nova kategorija"} onClose={onClose}>
      <div className="space-y-3 px-5 py-5">
        {save.isError && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{errMsg(save.error, "Čuvanje nije uspelo.")}</p>
        )}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700">Oznaka *</label>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="02.12" className={inputCls} />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700">Grupa</label>
            <input value={groupName} onChange={(e) => setGroupName(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Naziv kategorije *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700">Rok čuvanja (godina)</label>
            <input type="number" min={1} value={years} disabled={permanent} onChange={(e) => setYears(e.target.value)} className={inputCls} />
          </div>
          <label className="mt-7 flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={permanent} onChange={(e) => setPermanent(e.target.checked)} />
            Čuva se trajno
          </label>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Dopuna roka (npr. „posle isteka“)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Vrsta dokumenta koju HAFiskal sam broji</label>
          <select value={docType} onChange={(e) => setDocType(e.target.value)} className={inputCls}>
            <option value="">— ništa (ručni upis) —</option>
            {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Kategorija je aktivna
        </label>
      </div>
      <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
        <Button variant="secondary" onClick={onClose}>
          Otkaži
        </Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? "Čuvam..." : "Sačuvaj"}
        </Button>
      </div>
    </Modal>
  );
}

function CategoriesTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<EarhivCategoryDto | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const cats = useQuery({ queryKey: ["earhiv-categories"], queryFn: () => api.listEarhivCategories() });

  const template = useMutation({
    mutationFn: () => api.loadEarhivTemplate(),
    onSuccess: (r) => {
      setErr(null);
      setMsg(r.added > 0 ? `Dodato ${r.added} kategorija iz šablona.` : "Sve kategorije iz šablona već postoje.");
      qc.invalidateQueries({ queryKey: ["earhiv-categories"] });
    },
    onError: (e) => setErr(errMsg(e, "Preuzimanje šablona nije uspelo.")),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteEarhivCategory(id),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ["earhiv-categories"] });
    },
    onError: (e) => setErr(errMsg(e, "Brisanje nije uspelo.")),
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Lista kategorija sa rokovima čuvanja je preduslov za arhivsku knjigu. Šablon je polazna tačka: rok „P“ je naveden
        u propisima, „U“ je uobičajen u praksi – potvrdite ga ili izmenite. Lista se šalje nadležnom arhivu na saglasnost.
      </p>
      {msg && <p className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{msg}</p>}
      {err && <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</p>}
      <div className="flex gap-2">
        <Button onClick={() => template.mutate()} disabled={template.isPending}>
          {template.isPending ? "Preuzimam..." : "Preuzmi šablon"}
        </Button>
        <Button variant="secondary" onClick={() => setShowNew(true)}>
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          Nova kategorija
        </Button>
      </div>

      {cats.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : cats.data && cats.data.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-slate-500">
                <th className="px-3 py-2">Oznaka</th>
                <th className="px-3 py-2">Kategorija</th>
                <th className="px-3 py-2">Rok čuvanja</th>
                <th className="px-3 py-2">Izvor roka</th>
                <th className="px-3 py-2">HAFiskal popunjava</th>
                <th className="w-20 px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {cats.data.map((c) => (
                <tr key={c.id} className={`border-b border-border last:border-0 ${c.isActive ? "" : "opacity-50"}`}>
                  <td className="px-3 py-2 font-medium">{c.code}</td>
                  <td className="px-3 py-2">
                    {c.name}
                    {c.groupName && <div className="text-xs text-slate-400">{c.groupName}</div>}
                  </td>
                  <td className="px-3 py-2">{c.retentionLabel}</td>
                  <td className="px-3 py-2">{c.ruleSource === "P" ? "propis (P)" : c.ruleSource === "U" ? "uobičajeno (U)" : ""}</td>
                  <td className="px-3 py-2">{c.docType ? DOC_TYPE_LABELS[c.docType] : <span className="text-slate-400">ručno</span>}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2 text-slate-400">
                      <button onClick={() => setEditing(c)} className="hover:text-brand" aria-label="Izmeni">
                        <Pencil className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        onClick={() => window.confirm(`Obrisati kategoriju ${c.code}?`) && remove.mutate(c.id)}
                        className="hover:text-red-600"
                        aria-label="Obriši"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState icon={ListChecks} title="Lista kategorija je prazna" description="Kliknite „Preuzmi šablon“ da dobijete polaznu listu sa rokovima čuvanja." />
      )}

      {(showNew || editing) && (
        <CategoryModal
          cat={editing}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Podešavanja */

function SettingsTab() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["earhiv-settings"], queryFn: () => api.getEarhivSettings() });
  const [form, setForm] = useState<EarhivSettingsDto>({
    archiveName: "",
    responsiblePerson: "",
    consentNumber: "",
    consentDate: "",
    registeredAt: "",
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings.data) {
      setForm({
        archiveName: settings.data.archiveName ?? "",
        responsiblePerson: settings.data.responsiblePerson ?? "",
        consentNumber: settings.data.consentNumber ?? "",
        consentDate: settings.data.consentDate ?? "",
        registeredAt: settings.data.registeredAt ?? "",
      });
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api.saveEarhivSettings({
        archiveName: form.archiveName || null,
        responsiblePerson: form.responsiblePerson || null,
        consentNumber: form.consentNumber || null,
        consentDate: form.consentDate || null,
        registeredAt: form.registeredAt || null,
      }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["earhiv-settings"] });
    },
  });

  const set = (k: keyof EarhivSettingsDto) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  return (
    <div className="max-w-xl space-y-4">
      <p className="text-sm text-slate-500">
        Podaci o arhivu i odgovornom licu. Broj i datum saglasnosti arhiva na listu kategorija upisuju se u rubriku 7
        svakog reda knjige.
      </p>
      {save.isError && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{errMsg(save.error, "Čuvanje nije uspelo.")}</p>}
      {saved && <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">Podešavanja su sačuvana.</p>}
      <div>
        <label className="block text-sm font-medium text-slate-700">Nadležni arhiv</label>
        <input value={form.archiveName ?? ""} onChange={set("archiveName")} placeholder="npr. Istorijski arhiv Niš" className={inputCls} />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700">Odgovorno lice firme</label>
        <input value={form.responsiblePerson ?? ""} onChange={set("responsiblePerson")} className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700">Broj saglasnosti arhiva na listu</label>
          <input value={form.consentNumber ?? ""} onChange={set("consentNumber")} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">Datum saglasnosti</label>
          <input type="date" value={form.consentDate ?? ""} onChange={set("consentDate")} className={inputCls} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700">Datum prijave firme arhivu</label>
        <input type="date" value={form.registeredAt ?? ""} onChange={set("registeredAt")} className={inputCls} />
      </div>
      <Button onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending ? "Čuvam..." : "Sačuvaj"}
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ Stranica */

export function EarhivPage() {
  const [tab, setTab] = useState<TabKey>("knjiga");
  const gate = useQuery({ queryKey: ["earhiv-settings"], queryFn: () => api.getEarhivSettings(), retry: false });

  if (gate.isError) {
    if (gate.error instanceof ApiError && gate.error.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={gate.error} moduleKey={MODULE_KEY} />;
    }
    return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(gate.error, "Učitavanje nije uspelo.")}</p>;
  }

  const tabs: Array<{ key: TabKey; label: string; icon: typeof Archive }> = [
    { key: "knjiga", label: "Arhivska knjiga", icon: BookOpen },
    { key: "dokumenta", label: "Dokumenta", icon: FileUp },
    { key: "kategorije", label: "Lista kategorija", icon: ListChecks },
    { key: "podesavanja", label: "Podešavanja", icon: Settings2 },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">E-arhiv</h1>
        <p className="text-sm text-slate-500">
          Arhivska knjiga po obrascu AK, lista kategorija sa rokovima čuvanja i podaci za nadležni arhiv.
        </p>
      </div>
      <div className="flex gap-1 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.key ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <t.icon className="h-4 w-4" aria-hidden />
            {t.label}
          </button>
        ))}
      </div>
      {tab === "knjiga" && <BookTab />}
      {tab === "dokumenta" && <FilesTab />}
      {tab === "kategorije" && <CategoriesTab />}
      {tab === "podesavanja" && <SettingsTab />}
    </div>
  );
}
